import React, { useEffect, useState } from "react";
import axios from "../axiosConfig";
import Modal from "./Modal";
import ParticipantList from "./ParticipantList";
import Checkbox from "./Checkbox";
import ContentPresets from "./ContentPresets";
import DeadlineField from "./DeadlineField";
import {
  getLocalDatetimeInputValue,
  toUTCISOString,
  useFieldOptions,
  fieldOptionList,
} from "./Utils";

const storedUserSettings = () => {
  try {
    return JSON.parse(localStorage.getItem("user"))?.settings || null;
  } catch {
    return null;
  }
};

// The fields of a new task copied from an existing task/interaction: same
// content, format, type, participants and job; a deadline only if it had one,
// set to now.
export const taskCopyValues = (source) => ({
  content: source.content || "",
  format: source.format || "",
  type: source.type || "",
  participants: (source.participants || []).map((p) => ({ ...p })),
  hasDeadline: !!source.deadline,
  job_id: source.job_id,
});

// The "Create New Task" form, shared by TasksTable (new task, copy) and
// InteractionsTable (copy).
// Props:
//   isOpen, onClose
//   initialValues       – fields to start from (e.g. taskCopyValues(row)), or
//                         null for a blank task with the user's defaults
//   defaultParticipants – participants of a blank task
//   jobId               – job a blank task belongs to
//   onCreated           – called after the task is saved
const NewTaskModal = ({
  isOpen,
  onClose,
  initialValues = null,
  defaultParticipants = [],
  jobId,
  onCreated,
}) => {
  const fieldOptions = useFieldOptions();
  const [formData, setFormData] = useState(null);

  // Start afresh each time the form opens.
  useEffect(() => {
    if (!isOpen) return;
    const settings = storedUserSettings();
    setFormData({
      content: "",
      format: settings?.default_interaction_format || "",
      type: settings?.default_interaction_type || "",
      participants: defaultParticipants,
      hasDeadline: true,
      ...initialValues,
      job_id: initialValues?.job_id || jobId || null,
      deadlineLocal: getLocalDatetimeInputValue(),
    });
  }, [isOpen, initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      await axios.post(
        "/tasks/",
        {
          content: formData.content,
          format: formData.format,
          type: formData.type,
          participants: formData.participants.map((p) => ({
            id: p.id,
            type: p.type,
            is_sender: p.is_sender || false,
          })),
          deadline: formData.hasDeadline
            ? toUTCISOString(formData.deadlineLocal)
            : null,
          job_id: formData.job_id || null,
          completed_at: null,
        },
        { withCredentials: true },
      );
      onClose();
      if (onCreated) onCreated();
    } catch (err) {
      console.error("Failed to create task:", err);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Task">
      {isOpen && formData && (
        <form
          className="interaction-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="checkbox-group">
            <Checkbox
              name="hasDeadline"
              checked={formData.hasDeadline}
              onChange={handleFormChange}
              label="Set Deadline"
            />
          </div>

          {formData.hasDeadline && (
            <DeadlineField
              name="deadlineLocal"
              value={formData.deadlineLocal}
              onChange={handleFormChange}
            />
          )}

          <label>
            Format:
            <select
              autoComplete="off"
              name="format"
              value={formData.format}
              onChange={handleFormChange}
              required
            >
              <option value="">Select format...</option>
              {fieldOptionList(fieldOptions, "interactions", "format").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label>
            Type:
            <select
              autoComplete="off"
              name="type"
              value={formData.type}
              onChange={handleFormChange}
              required
            >
              <option value="">Select type...</option>
              {fieldOptionList(fieldOptions, "interactions", "type").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          <label>
            Content:
            <textarea
              autoComplete="off"
              name="content"
              value={formData.content}
              onChange={handleFormChange}
              rows={4}
              required
            />
          </label>
          <ContentPresets
            onSelect={(content) => setFormData((prev) => ({ ...prev, content }))}
          />

          <ParticipantList
            participants={formData.participants}
            onChange={(participants) =>
              setFormData((prev) => ({ ...prev, participants }))
            }
          />

          <div className="modal-footer">
            <button type="submit" className="submit-btn">
              Save
            </button>
            <button type="button" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default NewTaskModal;
